// React Imports
import { useState, useEffect, useMemo, useCallback } from "react";

// External Libraries
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { FileUpload } from "primereact/fileupload";
import type { FileUploadHandlerEvent } from "primereact/fileupload";

// Hooks & Helpers
import {
  buildUserProfilePayload,
  formatDeveloperProfile,
  normalizeAiToolSelections,
  normalizeFieldWithOther,
  normalizeToolingWithOther,
} from "../../../helpers/userProfile";
import type { ProfileFormData } from "../../../helpers/userProfile";
import { fetchCountriesList, uploadUserImage, updateUser } from "sj-ui-toolkit/src/api";
import type { CountryOption } from "sj-ui-toolkit/src/api/countries";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { useSettingsContext } from "sj-ui-toolkit/src/atlas/contexts/Settings/SettingsContextProvider";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useToast } from "sj-ui-toolkit/src/hooks";

// Local Components
import { ProfileField } from "./ProfileField";
import S from "./ProfileDetailsTab.module.scss";

// Constants
import {
  AI_TOOL_FIELDS,
  FIELD_GROUPS,
  PROFILE_FIELD_KEYS,
  SSO_LOCKED_FIELDS,
  ALWAYS_LOCKED_FIELDS,
} from "../../../constants/profileDetailsFields";
import { DeveloperProfileConstants } from "sj-ui-toolkit/src/constants/developerProfile";

export function ProfileDetailsTab() {
  useBreadcrumb({ label: "Profile", route: "/profile", display: "page" });
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData | null>(null);
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const { user, setUser, isLoading } = useUserContext();
  const { settings, settingsLoading } = useSettingsContext();
  const { showToast } = useToast();

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countriesList = await fetchCountriesList();
        setCountries(countriesList);
      } catch (e) {
        console.error("Error fetching countries:", e);
      }
    };

    loadCountries();
  }, []);

  const resetFormData = useCallback(() => {
    if (!user) return;

    const formatted = formatDeveloperProfile(user);
    const { tooling, toolingOther } = normalizeToolingWithOther(formatted.tooling as string[]);

    const aiToolSelections = normalizeAiToolSelections(user?.developerProfile);

    const validLanguageValues = DeveloperProfileConstants.OPTIONS.programmingLanguages.map(
      o => o.value
    );
    const { field: language, fieldOther: languageOther } = normalizeFieldWithOther(
      formatted.language,
      validLanguageValues
    );

    const validFunctionValues = DeveloperProfileConstants.OPTIONS.jobRoles.map(o => o.value);
    const { field: jobFunction, fieldOther: functionOther } = normalizeFieldWithOther(
      formatted.function,
      validFunctionValues
    );

    setFormData({
      ...formatted,
      language,
      languageOther,
      function: jobFunction,
      functionOther,
      toolingOther,
      tooling,
      ...aiToolSelections,
      sso: settings?.sso,
      allowProfileEditing: settings?.allowUserProfileEdits,
    } as ProfileFormData);
  }, [user, settings]);

  useEffect(() => {
    if (isEditing) return;
    resetFormData();
  }, [resetFormData, isEditing]);

  // Clear language and tooling fields when user selects "does not work with code"
  useEffect(() => {
    if (!formData) return;

    const expertise = formData.expertise;

    if (expertise === DeveloperProfileConstants.VALUES.EXPERTISE_DOES_NOT_WORK_WITH_CODE) {
      setFormData(prev => ({
        ...prev!,
        language: "",
        languageOther: "",
        additionalLanguages: [],
        tooling: [],
        toolingOther: "",
      }));
    }
  }, [formData?.expertise]);

  const disableSave = useMemo(() => {
    if (!formData || !isEditing) return false;

    const { language, tooling, languageOther, toolingOther } = formData;
    const languageOtherSelectedAndMissing =
      language === "other" && !(languageOther as string)?.trim();

    const toolingOtherSelectedAndMissing =
      Array.isArray(tooling) && tooling.includes("other") && !(toolingOther as string)?.trim();

    const functionOtherSelectedAndMissing =
      formData.function === "other" && !(formData.functionOther as string)?.trim();

    return (
      languageOtherSelectedAndMissing ||
      toolingOtherSelectedAndMissing ||
      functionOtherSelectedAndMissing
    );
  }, [formData, isEditing]);

  const formDisabled = useMemo(() => {
    if (!settings) return false;

    const { sso, allowUserProfileEdits } = settings;

    if (sso) {
      // SSO on: allowProfileEdits takes precedence
      return !allowUserProfileEdits;
    }

    // SSO off: if allowProfileEdits is false, disable
    if (!allowUserProfileEdits) {
      return true;
    }

    return false;
  }, [settings]);

  const shouldDisableField = (fieldKey: string): boolean => {
    const expertise = formData?.expertise;

    if (expertise === DeveloperProfileConstants.VALUES.EXPERTISE_DOES_NOT_WORK_WITH_CODE) {
      return (
        [
          PROFILE_FIELD_KEYS.LANGUAGE,
          PROFILE_FIELD_KEYS.ADDITIONAL_LANGUAGES,
          PROFILE_FIELD_KEYS.TOOLING,
        ] as string[]
      ).includes(fieldKey);
    }

    return false;
  };

  const isFieldLocked = (fieldKey: string, isFormDisabled: boolean): boolean => {
    if (ALWAYS_LOCKED_FIELDS.includes(fieldKey as (typeof ALWAYS_LOCKED_FIELDS)[number]))
      return true;
    if (
      isFormDisabled &&
      SSO_LOCKED_FIELDS.includes(fieldKey as (typeof SSO_LOCKED_FIELDS)[number])
    )
      return true;
    return false;
  };

  const applyFieldOptions = (
    field: (typeof FIELD_GROUPS)[number]["fields"][number] & { locked: boolean; disabled: boolean },
    countryList: { label: string; value: string }[]
  ) => {
    if (field.key === PROFILE_FIELD_KEYS.COUNTRY) return { ...field, options: countryList };
    return field;
  };

  const dynamicFieldGroups = useMemo(() => {
    return FIELD_GROUPS.map(group => ({
      ...group,
      fields: group.fields.map(field =>
        applyFieldOptions(
          {
            ...field,
            locked: isFieldLocked(field.key, formDisabled),
            disabled: shouldDisableField(field.key),
          },
          countries
        )
      ),
    }));
  }, [countries, formDisabled, formData?.expertise]);

  const handleChange = (field: string, value: unknown) =>
    setFormData(prev => {
      if (!prev) return prev;
      const updates: Record<string, unknown> = { [field]: value };

      // "I don't use AI tools" clears any categorized AI selections
      if (field === PROFILE_FIELD_KEYS.DOES_NOT_USE_AI && value) {
        AI_TOOL_FIELDS.forEach(aiField => {
          updates[aiField.key] = [];
        });
      }

      return { ...prev, ...updates } as ProfileFormData;
    });

  const handleProfilePhotoUpload = async ({ files, options }: FileUploadHandlerEvent) => {
    try {
      const file = files?.[0];
      if (!file) return;
      setIsUploadingPhoto(true);

      const response = (await uploadUserImage(file)) as { small_image_url: string };
      const { small_image_url: smallImageUrl } = response;

      if (user) {
        setUser({
          ...user,
          railsProfile: {
            ...user.railsProfile,
            smallImageUrl,
          },
        } as typeof user);
      }

      if (options?.clear) options.clear();
    } catch (error) {
      console.error("Error uploading profile photo:", error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to upload profile photo. Please try again.",
        life: 5000,
      });
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleCancelEditClick = () => {
    resetFormData();
    setIsEditing(false);
  };

  const handleEditProfileClick = async () => {
    if (!isEditing) return setIsEditing(true);

    const payload = buildUserProfilePayload(formData!);

    try {
      await updateUser(payload);
      if (user) {
        setUser({ ...user, ...payload });
      }
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to save profile changes. Please try again.",
        life: 5000,
      });
    }
  };

  if (!formData) return null;

  return (
    <>
      <div className="my-5 flex gap-3">
        <Button
          label={isEditing ? "Save" : "Edit Profile"}
          onClick={handleEditProfileClick}
          icon={!isEditing && "pi pi-pencil"}
          disabled={disableSave || isLoading || settingsLoading}
          data-testid="profile-details-edit-save-button"
        />
        {isEditing && (
          <Button
            label="Cancel"
            outlined
            severity="secondary"
            onClick={handleCancelEditClick}
            data-testid="profile-details-cancel-button"
          />
        )}
        <FileUpload
          mode="basic"
          name="profilePhoto"
          accept="image/png, image/jpeg"
          maxFileSize={5000000}
          chooseLabel={isUploadingPhoto ? "Uploading..." : "Upload profile photo"}
          disabled={isUploadingPhoto}
          customUpload
          auto
          uploadHandler={handleProfilePhotoUpload}
          chooseOptions={{
            icon: isUploadingPhoto ? "pi pi-spin pi-spinner" : "pi pi-upload",
            className: "p-button-outlined p-button-secondary",
          }}
          pt={{
            input: { "aria-label": "Upload profile photo" },
            basicButton: { "data-testid": "profile-photo-upload-button" },
          }}
        />
      </div>
      <div className={S.fieldsGrid}>
        {dynamicFieldGroups.map((group, groupIndex) => (
          <div key={groupIndex}>
            {group.title && <h2 className="atlas-h5 mb-4">{group.title}</h2>}
            <div className="grid grid-cols-2 gap-10">
              {group.fields.map(field => (
                <ProfileField
                  key={field.key}
                  field={field}
                  formData={formData}
                  isEditing={isEditing}
                  handleChange={handleChange}
                />
              ))}
            </div>

            {/* Divider */}
            {groupIndex !== FIELD_GROUPS.length - 1 && <hr className="my-12 border-gray-400" />}
          </div>
        ))}
        <div>
          <div role="group" aria-labelledby="ai-usage-heading">
            <h2 id="ai-usage-heading" className="atlas-h5 mb-4">
              AI Usage
            </h2>
            {isEditing && (
              <div className="flex items-center gap-2 my-3">
                <Checkbox
                  inputId="doesNotUseAi"
                  checked={formData.doesNotUseAi as boolean}
                  onChange={e => handleChange(PROFILE_FIELD_KEYS.DOES_NOT_USE_AI, e.checked)}
                  pt={{ input: { "data-testid": "profile-details-does-not-use-ai-checkbox" } }}
                />
                <label className="body-md" htmlFor="doesNotUseAi">
                  I don't use AI tools
                </label>
              </div>
            )}
            {!isEditing && formData.doesNotUseAi ? (
              <div className={S.profileDetailText}>I do not use AI</div>
            ) : (
              <div className="grid grid-cols-2 gap-10">
                {AI_TOOL_FIELDS.map(field => (
                  <ProfileField
                    key={field.key}
                    field={{ ...field, disabled: formData.doesNotUseAi as boolean }}
                    formData={formData}
                    isEditing={isEditing}
                    handleChange={handleChange}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Divider */}
          <hr className="my-12 border-gray-400" />
        </div>
      </div>
    </>
  );
}
